import settings from '../config/settings';
import { getAuthHeaders } from './api';
import { saveProductsToCache, getProductsFromCache, needsCacheRefresh } from './productCache';
import { saveUsersToCache, needsUserCacheRefresh } from './userCache';

/**
 * Daily catalog cache warming.
 *
 * The kiosk always reads users/products LIVE from the backend as the primary path.
 * These caches are a last-known-good snapshot, refreshed at most once per 24h, used
 * ONLY as a fallback when a live call fails (e.g. a network blip). Warming is
 * fire-and-forget and never throws, so it can never affect a payment flow.
 */

const API_BASE_URL = settings.api.baseUrl || '';

// Both /kiosk-products and /kiosk-users load the full list from the DB and paginate
// in memory; an oversized per_page returns everything in a single request.
const FULL_PAGE = 100000;

async function fetchFullList(endpoint) {
  const url = new URL(endpoint, API_BASE_URL);
  url.searchParams.set('per_page', String(FULL_PAGE));
  url.searchParams.set('page', '1');
  const response = await fetch(url.toString(), {
    headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache', ...getAuthHeaders() },
    signal: AbortSignal.timeout(30000)
  });
  if (!response.ok) throw new Error(`${endpoint} -> ${response.status} ${response.statusText}`);
  return response.json();
}

// Keep only the fields the kiosk needs for search + order billing, so 6k+ donor
// records stay well under the localStorage quota.
function trimUser(u) {
  const b = u.billing || {};
  return {
    id: u.id,
    customer_id: u.customer_id != null ? u.customer_id : u.id,
    first_name: u.first_name || u.firstName || '',
    last_name: u.last_name || u.lastName || '',
    name: u.name || '',
    email: u.email || '',
    phone: u.phone || b.phone || '',
    billing: {
      first_name: b.first_name || '',
      last_name: b.last_name || '',
      email: b.email || '',
      phone: b.phone || '',
      address_1: b.address_1 || '',
      city: b.city || '',
      state: b.state || '',
      postcode: b.postcode || '',
      country: b.country || 'US'
    }
  };
}

/**
 * Refresh each cache if missing or older than its 24h TTL. Safe to call on every
 * app load — it no-ops when the caches are still fresh. Never throws.
 */
export async function warmCatalogCaches() {
  // Products (small; ~438 rows)
  try {
    if (needsCacheRefresh()) {
      const data = await fetchFullList('/kiosk-products');
      const products = Array.isArray(data) ? data : (Array.isArray(data?.products) ? data.products : []);
      if (products.length) {
        saveProductsToCache(products);
        console.log(`[cacheWarm] Products cache refreshed (${products.length})`);
      }
    }
  } catch (e) {
    console.warn('[cacheWarm] Product cache warm skipped:', e?.message || e);
  }

  // Users (large; trimmed before storing)
  try {
    if (needsUserCacheRefresh()) {
      const data = await fetchFullList('/kiosk-users');
      const users = Array.isArray(data?.users) ? data.users : (Array.isArray(data) ? data : []);
      if (users.length) {
        // saveUsersToCache is try/catch'd internally; a quota failure returns false, not a throw.
        const ok = saveUsersToCache(users.map(trimUser));
        console.log(`[cacheWarm] Users cache ${ok ? 'refreshed' : 'save failed (quota?)'} (${users.length})`);
      }
    }
  } catch (e) {
    console.warn('[cacheWarm] User cache warm skipped:', e?.message || e);
  }
}

/**
 * Fallback product search over the cached snapshot (used only when the live
 * /api/products call fails). Matches name/sku, case-insensitive.
 */
export function searchCachedProducts(term) {
  const cache = getProductsFromCache();
  const products = cache?.products;
  if (!Array.isArray(products) || products.length === 0) return null; // no snapshot to fall back to
  const t = String(term || '').trim().toLowerCase();
  if (!t) return products;
  return products.filter((p) => {
    const name = String(p?.name || '').toLowerCase();
    const sku = String(p?.sku || '').toLowerCase();
    return name.includes(t) || sku.includes(t);
  });
}
